/**
 * Tactics pattern library — localStorage-backed v1 storage.
 *
 * TODO(Phase 4 v2): replace this localStorage layer with the
 *   `tactics_patterns` Prisma table per
 *   `docs/scope/tactics-hub-roadmap.md` §Phase 4. The type shape
 *   defined here is the source of truth for the SQL migration —
 *   the table columns mirror these fields one-for-one.
 *
 * Scope: named, recallable tactics patterns ("Tuesday press",
 * "switch the point of attack") that snapshot a TacticsLayout
 * plus metadata (phase of play, formation, tags, curator).
 * Mirrors `tactics3dStore.ts` for the per-board live state but
 * differs in shape: patterns are addressed by an opaque `id`
 * (not a persistKey) and carry library-friendly metadata.
 *
 * Public API:
 *   - listPatterns(filter?)  — read-only filtered view
 *   - savePattern(pattern)   — upsert (create on first save, update on subsequent)
 *   - getPattern(id)         — full pattern incl. layout
 *   - deletePattern(id)      — hard-delete (v2 will soft-delete via deleted_at)
 */

import type { TacticsLayout } from '@4cm/core/tactics';
import { getFamousTacticsPatterns } from './famousTactics';

const STORE_KEY = '4cm_tactics_patterns';

export type TacticsPatternPhase =
  | 'attacking'
  | 'defending'
  | 'transitions'
  | 'individual';

export type TacticsPatternSource = 'PLATFORM' | 'CLUB' | 'CURATED_EXPERT';

/** Library-ready snapshot of a TacticsLayout plus metadata. */
export interface TacticsPattern {
  id: string;
  name: string;
  description?: string;
  /** Free-form labels; filters lowercase both sides on compare. */
  tags: string[];
  phaseOfPlay: TacticsPatternPhase;
  /** Formation code at save time — '4-3-3', '4-2-3-1', etc. */
  formation: string;
  /** The actual TacticsLayout snapshot. */
  layout: TacticsLayout;
  /** Provenance — same discipline as Drills (§Phase 4 risks block). */
  sourceType: TacticsPatternSource;
  /** Optional human curator name for CURATED_EXPERT patterns. */
  curatorName?: string;
  /** ISO timestamps. */
  createdAt: string;
  updatedAt: string;
}

/** Optional filter for the list query. All filters AND together. */
export interface ListPatternsFilter {
  tags?: string[];
  phaseOfPlay?: TacticsPatternPhase;
  formation?: string;
  /** Cap on result count. Default: unlimited. */
  limit?: number;
}

type Store = Record<string, TacticsPattern>;

function readStore(): Store {
  try {
    const raw =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(STORE_KEY)
        : null;
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Store;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStore(store: Store): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch (err) {
    console.warn('[tacticsPatternsStore] could not persist', err);
  }
}

/** Lowercase a string list for case-insensitive compare. */
function lc(arr: readonly string[]): string[] {
  return arr.map((s) => s.toLowerCase());
}

/**
 * List patterns matching the filter. Returns patterns sorted by
 * `updatedAt` descending (most recently touched first).
 */
export function listPatterns(filter: ListPatternsFilter = {}): TacticsPattern[] {
  const store = readStore();
  const all = Object.values(store);

  const wantTags = filter.tags ? lc(filter.tags) : null;

  const matched = all.filter((p) => {
    if (filter.phaseOfPlay && p.phaseOfPlay !== filter.phaseOfPlay) return false;
    if (filter.formation && p.formation !== filter.formation) return false;
    if (wantTags && wantTags.length > 0) {
      const have = lc(p.tags);
      const hit = wantTags.every((t) => have.includes(t));
      if (!hit) return false;
    }
    return true;
  });

  matched.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  if (typeof filter.limit === 'number' && filter.limit > 0) {
    return matched.slice(0, filter.limit);
  }
  return matched;
}

/** Full pattern by id; null when not found.
 *
 *  Falls back to the in-memory famous-tactics list. The PatternDrawer
 *  shows famous + user-saved in one list; the apply handler reaches
 *  back through this function, so famous lookups need to resolve here
 *  or applying a famous tactic becomes a silent no-op. */
export function getPattern(id: string): TacticsPattern | null {
  if (!id) return null;
  const store = readStore();
  const owned = store[id];
  if (owned) return owned;
  return getFamousTacticsPatterns().find((p) => p.id === id) ?? null;
}

/** Input shape for savePattern — auto-fields (id, createdAt, updatedAt) optional. */
export interface SavePatternInput {
  /** Optional id — when present, performs an update; otherwise a new id is minted. */
  id?: string;
  name: string;
  description?: string;
  tags?: string[];
  phaseOfPlay: TacticsPatternPhase;
  formation: string;
  layout: TacticsLayout;
  sourceType?: TacticsPatternSource;
  curatorName?: string;
}

/**
 * Upsert a pattern. Returns the persisted shape (so the caller can
 * read back the minted id + timestamps).
 */
export function savePattern(input: SavePatternInput): TacticsPattern {
  const store = readStore();
  const now = new Date().toISOString();
  const id = input.id ?? mintId();
  const prior = store[id];
  const entry: TacticsPattern = {
    id,
    name: input.name,
    description: input.description,
    tags: input.tags ?? [],
    phaseOfPlay: input.phaseOfPlay,
    formation: input.formation,
    layout: input.layout,
    sourceType: input.sourceType ?? 'CLUB',
    curatorName: input.curatorName,
    createdAt: prior?.createdAt ?? now,
    updatedAt: now,
  };
  store[id] = entry;
  writeStore(store);
  return entry;
}

/** Hard-delete a pattern. No-op when the id isn't present. */
export function deletePattern(id: string): void {
  const store = readStore();
  if (!(id in store)) return;
  delete store[id];
  writeStore(store);
}

/** Stable id factory — Node 20+ and modern browsers expose crypto.randomUUID. */
function mintId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for the rare environment without crypto.randomUUID. We still
  // want a stable-ish unique id — the timestamp + random is fine for v1
  // since this path only fires in older test runners.
  return `pat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
